import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import LeaderboardCard from './LeaderboardCard';
import { useNavigate } from 'react-router-dom';
import { environment } from '../config/environment';

type Phase = 'LOBBY' | 'COUNTDOWN' | 'READING' | 'QUESTION' | 'REVEAL' | 'ACHIEVEMENTS' | 'INTERMISSION' | 'PODIUM';

interface Player {
  id: string;
  name: string;
  score: number;
}

interface QuestionPush {
  index: number;
  total: number;
  id: string;
  text: string;
  choices: string[];
  secondsLeft: number;
}

interface RevealPush {
  questionId: string;
  correctIdx: number;
  evidence: string;
}

interface Achievement {
  playerId: string;
  playerName: string;
  type: string;
  title: string;
  description: string;
  bonusPoints: number;
}

interface PhaseContentProps {
  phase: Phase;
  wsConnected: boolean;
  gamePin?: string;
  players?: Player[];
  countdownMessage?: string;
  readingQuestion?: string;
  currentQuestion?: QuestionPush | null;
  timeLeft?: number;
  reveal?: RevealPush | null;
  achievements?: Achievement[];
  leaderboard?: Player[];
  winners?: Player[];
  gameStatistics?: any;
}

const PhaseContent: React.FC<PhaseContentProps> = ({
  phase,
  wsConnected,
  gamePin = '',
  players = [],
  countdownMessage,
  readingQuestion,
  currentQuestion,
  timeLeft,
  reveal,
  achievements,
  leaderboard,
  winners,
  gameStatistics
}) => {
  const navigate = useNavigate();

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading/Connecting State
  if (!wsConnected && phase !== 'PODIUM') {
    return (
      <div className="bg-white rounded-lg p-12 border border-gray-200 text-center">
        <div className="text-6xl mb-4">⏳</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Setting up game server</h2>
        <p className="text-gray-600 mb-6">Please wait while we prepare your game</p>
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  // LOBBY - Split-screen join instructions
  if (phase === 'LOBBY') {
    const playerUrl = `${environment.playerUrl}/${gamePin}`;

    return (
      <div className="space-y-6">
        {/* Player Counter */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 text-center">
          <div className="text-gray-600 text-sm font-semibold mb-2">Waiting for Players</div>
          <div className="text-6xl font-black text-orange-500 mb-2">
            {players?.length || 0}
          </div>
          <div className="text-gray-700 text-lg">
            {(players?.length || 0) === 1 ? 'player ready' : 'players ready'}
          </div>
        </div>

        {/* Join Instructions */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-900">How to Join</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-0 divide-x divide-gray-200">
            {/* QR Code Section */}
            <div className="p-8">
              <div className="text-center space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">Scan to Join</h4>

                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <QRCodeSVG
                      value={playerUrl}
                      size={180}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-500 font-mono break-all">
                  {playerUrl}
                </p>
              </div>
            </div>

            {/* Manual Entry Section */}
            <div className="p-8">
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Enter PIN</h4>
                  <div className="bg-orange-50 rounded-lg p-6 border border-orange-200 text-center">
                    <div className="text-sm text-gray-600 mb-2">Game PIN</div>
                    <div className="text-5xl font-black text-orange-600 tracking-wider">
                      {gamePin}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">Join URL</div>
                  <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                    <p className="text-gray-900 font-medium break-all">
                      {environment.playerUrl.replace('https://', '').replace('http://', '')}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    navigator.clipboard.writeText(playerUrl);
                  }}
                  className="w-full px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-md hover:shadow-lg"
                >
                  Copy Join Link
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // COUNTDOWN
  if (phase === 'COUNTDOWN') {
    return (
      <div className="bg-white rounded-lg p-12 border border-gray-200 text-center">
        <div className="text-8xl mb-6 animate-pulse">{countdownMessage === 'GO!' ? '🚀' : '⏱️'}</div>
        <h2 className="text-6xl font-black text-gray-900 mb-4 animate-bounce">{countdownMessage}</h2>
        <p className="text-2xl text-gray-600 font-semibold">Game starting...</p>
      </div>
    );
  }

  // READING
  if (phase === 'READING') {
    return (
      <div className="bg-white rounded-lg p-8 border border-gray-200">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4 animate-pulse">📖</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Players Reading Question</h2>
          <p className="text-gray-600">Please wait while players read the question</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-8 border-2 border-gray-200">
          <h3 className="text-3xl font-bold text-gray-900 text-center">{readingQuestion}</h3>
        </div>
      </div>
    );
  }

  // QUESTION
  if (phase === 'QUESTION' && currentQuestion) {
    return (
      <div className="bg-white rounded-lg p-8 border border-gray-200">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg text-gray-600">Question {currentQuestion.index} of {currentQuestion.total}</span>
            <span className="text-3xl font-bold text-gray-900">
              {formatTime(timeLeft || 0)}
            </span>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">{currentQuestion.text}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {currentQuestion.choices.map((choice, index) => (
            <div
              key={index}
              className="bg-gray-100 rounded-lg p-6 text-center border-2 border-gray-300"
            >
              <span className="font-semibold text-gray-900 text-lg">{choice}</span>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-600">
          Waiting for players to answer. The game will continue automatically.
        </p>
      </div>
    );
  }

  // REVEAL
  if (phase === 'REVEAL' && currentQuestion && reveal) {
    return (
      <div className="bg-white rounded-lg p-8 border border-gray-200">
        <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">Answer Revealed</h3>
        <h4 className="text-xl text-gray-700 mb-6 text-center">{currentQuestion.text}</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {currentQuestion.choices.map((choice, index) => (
            <div
              key={index}
              className={`rounded-lg p-6 text-center border-2 ${
                index === reveal.correctIdx
                  ? 'bg-green-100 border-green-500 text-green-900'
                  : 'bg-gray-100 border-gray-300 text-gray-600'
              }`}
            >
              <span className="font-semibold text-lg">{choice}</span>
              {index === reveal.correctIdx && (
                <span className="ml-2 text-2xl">✓</span>
              )}
            </div>
          ))}
        </div>

        {reveal.evidence && (
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-4">
            <p className="text-blue-900 text-center">{reveal.evidence}</p>
          </div>
        )}

        <p className="text-center text-gray-600">
          Calculating achievements...
        </p>
      </div>
    );
  }

  // ACHIEVEMENTS
  if (phase === 'ACHIEVEMENTS') {
    if (!achievements || achievements.length === 0) {
      return (
        <div className="bg-white rounded-lg p-12 border border-gray-200 text-center">
          <div className="text-6xl mb-6">⏳</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Great Round!</h3>
          <p className="text-gray-600">Preparing next question...</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg p-8 border border-gray-200">
        <h3 className="text-4xl font-bold text-gray-900 mb-8 text-center">🏆 Achievements! 🏆</h3>
        <div className="space-y-4">
          {achievements.map((achievement, index) => (
            <div
              key={index}
              className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border-2 border-purple-400 shadow-lg animate-pulse"
              style={{
                animationDelay: `${index * 0.3}s`,
                animationDuration: '1s',
                animationIterationCount: '3'
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-purple-900 mb-2">{achievement.title}</h4>
                  <p className="text-lg text-gray-800 mb-2">{achievement.description}</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {achievement.playerName}
                  </p>
                </div>
                <div className="text-center ml-6 bg-white rounded-lg p-4 border-2 border-green-500">
                  <div className="text-4xl font-black text-green-600">
                    +{achievement.bonusPoints}
                  </div>
                  <div className="text-sm text-gray-700 font-medium">bonus</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // INTERMISSION
  if (phase === 'INTERMISSION') {
    return (
      <div className="bg-white rounded-lg p-8 border border-gray-200">
        <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">Leaderboard</h3>

        <div className="space-y-3">
          {leaderboard && leaderboard.length > 0 ? leaderboard.map((player, index) => (
            <LeaderboardCard
              key={player.id}
              player={player}
              rank={index + 1}
              variant="leaderboard"
            />
          )) : (
            <p className="text-center text-gray-600">No players yet</p>
          )}
        </div>
      </div>
    );
  }

  // PODIUM
  if (phase === 'PODIUM') {
    return (
      <div className="bg-white rounded-lg p-8 border border-gray-200">
        <div className="text-center mb-8">
          <div className="text-8xl mb-4">🏆</div>
          <h3 className="text-4xl font-bold text-gray-900 mb-4">Game Complete!</h3>
          <p className="text-gray-600">The game server will shut down automatically.</p>
        </div>

        <div className="space-y-4 mb-8">
          {winners && winners.slice(0, 3).map((player, index) => (
            <LeaderboardCard
              key={player.id}
              player={player}
              rank={index + 1}
              variant="podium"
            />
          ))}
        </div>

        <div className="text-center space-x-3">
          {gameStatistics && (
            <button
              onClick={() => navigate('/quiz-statistics', { state: { statistics: gameStatistics } })}
              className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-all"
            >
              View Statistics
            </button>
          )}
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="bg-white rounded-lg p-8 border border-gray-200 text-center">
      <p className="text-gray-600">Loading game phase...</p>
    </div>
  );
};

export default PhaseContent;
